'use client'

import { ScanSearch, ArrowRight, Bug, Package, Droplets, Stethoscope, Layers } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { analytics, ctaSurfaceProps, trackEvent, type EventProperties } from '@/lib/analytics'
import { useImpressionEvent } from '@/hooks/useImpressionEvent'
import {
  isDiagnosisPost,
  DIAGNOSE_CTA,
  matchedDiagnoseKeyword,
  diagnoseHeadingFor,
  DIAGNOSE_INLINE_TAGLINE,
  WEED_FALLBACK_SYMPTOM_KEY,
} from '@/lib/blog-diagnose-cta'
import { IDENTIFY_CTA } from '@/lib/identify-cta'
import { isWeedPost, weedIdentifierHref, WEED_IDENTIFIER_CTA } from '@/lib/weed-identifier-cta'
import {
  isGrassIdentityIntent,
  isIdentifyV2Slug,
  matchedIdentifyKeyword,
  identifyCopyFor,
  isEarlyCtaPost,
} from '@/lib/blog-intent'
import { useFeatureFlagVariantKey } from 'posthog-js/react'
import { useWeedIdCapability } from '@/hooks/use-weed-id-capability'
import { InlineScanCta } from '@/components/blog/inline-scan-cta'
import { ToolIllustration, hasToolIllustration } from '@/components/tools/tool-illustration'
import { resolveContextualTool, type ToolIconKey } from '@/lib/blog-contextual-tool'

interface ContextualToolCTAProps {
  slug?: string
  category?: string
  tags?: string[]
  /**
   * Analytics slot marker. The early-CTA pilot renders this same surface near
   * the top of a post; passing slot='early' tags every event on that instance
   * with `slot: 'early'` so it stays separable from the mid-content series
   * WITHOUT renaming any cta_location (analytics-continuity rule).
   */
  slot?: 'early'
}

/** Deliverable preview chips for the diagnose picker (approved mockup V2). */
const DIAGNOSE_PREVIEW_CHIPS = ['Likely cause with confidence', 'What to apply and when']

/** Deliverable preview chips for the identify picker (V2 experiment arm). */
const IDENTIFY_V2_PREVIEW_CHIPS = ['Grass type with confidence', 'Care plan for your ZIP']

const TOOL_ICONS: Record<ToolIconKey, LucideIcon> = {
  scan: ScanSearch,
  bug: Bug,
  package: Package,
  droplets: Droplets,
  stethoscope: Stethoscope,
  layers: Layers,
}

interface ToolRec {
  href: string
  label: string
  tagline: string
  icon: LucideIcon
}

/**
 * Routing lives in lib/blog-contextual-tool.ts so the server-side content
 * pipeline can ask the same question. This only re-attaches the icon component.
 */
function getContextualTool(slug?: string, category?: string, tags?: string[]): ToolRec | null {
  const tool = resolveContextualTool(slug, category, tags)
  return tool ? { ...tool, icon: TOOL_ICONS[tool.icon] } : null
}

// Light glass banner treatment from the "Tool CTA" design (Claude Design
// Paywall project). Frosted white card + glass icon tile + gradient pill CTA.
const bannerClass =
  'my-8 px-5 py-[18px] rounded-2xl flex items-center gap-4 bg-linear-to-b from-white/70 to-white/55 backdrop-blur-xl backdrop-saturate-150 border border-white/65 [box-shadow:0_12px_40px_rgba(22,163,74,0.10),inset_0_1px_0_rgba(255,255,255,0.85)] hover:[box-shadow:0_20px_60px_rgba(22,163,74,0.14),inset_0_1px_0_rgba(255,255,255,0.85)] transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 group cursor-pointer'

const iconTileClass =
  'grid place-items-center flex-shrink-0 w-[52px] h-[52px] rounded-[14px] bg-linear-to-b from-white/90 to-green-50/75 border border-white/80 [box-shadow:0_6px_16px_rgba(22,163,74,0.14),inset_0_1px_0_rgba(255,255,255,0.9)]'

const ctaPillClass =
  'inline-flex items-center justify-center gap-2 flex-shrink-0 min-h-[44px] px-5 rounded-full border border-white/35 text-white text-[15px] font-bold tracking-tight bg-linear-to-r from-green-600/90 to-emerald-600/90 backdrop-blur-sm [box-shadow:0_8px_20px_-6px_rgba(22,163,74,0.45),inset_0_1px_0_rgba(255,255,255,0.35)] group-hover:[box-shadow:0_12px_26px_-6px_rgba(22,163,74,0.55),inset_0_1px_0_rgba(255,255,255,0.4)] transition-shadow'

export function ContextualToolCTA({ slug, category, tags, slot }: ContextualToolCTAProps) {
  const { capability } = useWeedIdCapability()
  // Experiment: keyword-matched identify copy + deliverable chips on the inline
  // scan CTA. Called UNCONDITIONALLY, before every early return below
  // (rules-of-hooks). Fail-safe like inline-product-callout: any value other
  // than "test" — undefined before flags resolve, off-prod where PostHog is
  // disabled, or a stray future variant — renders the control arm.
  const identifyFlag = useFeatureFlagVariantKey('blog_identify_cta_v2')
  // Scoped to an explicit slug allowlist so every other identify post renders
  // byte-identically to before, experiment props included.
  const inIdentifyExperiment = isIdentifyV2Slug(slug)
  const identifyVariant = identifyFlag === 'test' ? 'test' : 'control'
  const showIdentifyV2 = inIdentifyExperiment && identifyVariant === 'test'
  const identifyKey = showIdentifyV2 ? matchedIdentifyKeyword(undefined, slug) : null
  const identifyV2Copy = showIdentifyV2 ? identifyCopyFor(identifyKey) : null

  const selectedTool = getContextualTool(slug, category, tags)
  // Weed post with the identifier's analysis capability off. Tracked separately
  // from the tool swap because the diagnose branch below needs weed-specific
  // copy: these readers have a weed, not a lawn symptom.
  const isWeedFallback =
    !!selectedTool?.href.startsWith('/weed-identifier') && !capability.analysisEnabled
  const tool = isWeedFallback
    ? { href: DIAGNOSE_CTA.href, label: DIAGNOSE_CTA.label, tagline: DIAGNOSE_CTA.tagline, icon: Stethoscope }
    : selectedTool

  // Slot + cohort ride every event on this instance as additive properties so
  // the early-CTA pilot is separable from the mid-content series (no rename).
  //
  // Both are now always emitted, where `slot` used to be omitted for
  // mid-content. Measured 2026-08-09: allowlisted posts convert 5.33% into a
  // funnel vs 2.58% elsewhere, but that 2.1x is confounded — the allowlist was
  // hand-picked for funnel intent, so intent and placement move together. The
  // control that separates them is the MID CTA on an allowlisted post versus
  // the same slot on a non-allowlisted one, and that comparison is only
  // possible if the mid instance also reports both properties.
  const slotProps: EventProperties = {
    slot: slot ?? 'mid',
    early_cta_post: slug ? isEarlyCtaPost(slug) : false,
  }

  // Both experiment arms tag the SAME cta_location with additive props so the
  // arms are queryable off one event. Undefined off-allowlist (with no slot) so
  // non-enrolled posts emit exactly the property bag they always have.
  const identifyExperimentProps: EventProperties | undefined = !inIdentifyExperiment
    ? undefined
    : showIdentifyV2
      ? {
          cta_variant: 'v2_identify',
          ...(identifyKey ? { identify_key: identifyKey } : {}),
          experiment_variant: identifyVariant,
        }
      : { cta_variant: 'v1_generic', experiment_variant: 'control' }

  const identifyAnalyticsProps: EventProperties = {
    ...slotProps,
    ...identifyExperimentProps,
  }

  // If no contextual match, show the default Grass ID CTA -- now an inline
  // upload affordance: the picker opens on the post itself and the chosen photo
  // rides the client-side navigation to the homepage flow (2.2% blog-to-upload
  // baseline was measured WITH the old link banner; the trip was the friction).
  if (!tool) {
    return (
      <InlineScanCta
        ctaLocation={IDENTIFY_CTA.location.blogInlineDefault}
        heading={identifyV2Copy?.heading ?? 'What type of grass do you have?'}
        tagline={identifyV2Copy?.tagline ?? 'Snap a photo right here and find out in seconds'}
        previewChips={showIdentifyV2 ? IDENTIFY_V2_PREVIEW_CHIPS : undefined}
        analyticsProps={identifyAnalyticsProps}
      />
    )
  }

  // Contextual tool CTA
  const isDiagnose = tool.href === DIAGNOSE_CTA.href
  const isWeedIdentifier = tool.href.startsWith('/weed-identifier')
  // Identify sub-cases (grass-type + the deliberately-reframed Scotts branch) both
  // route to the grass-ID flow, so they carry the identify destination fields;
  // tool/diagnose destinations are out of scope for this instrumentation pass.
  const isIdentify = tool.href === IDENTIFY_CTA.href
  const ctaLocation =
    isIdentify
      ? (slug?.toLowerCase().includes('scotts')
          ? 'blog_inline_scotts_app_cta'
          : IDENTIFY_CTA.location.blogInlineGrassType)
      : isDiagnose
        ? DIAGNOSE_CTA.location.inline
        : isWeedIdentifier
          ? WEED_IDENTIFIER_CTA.location.inline
        : 'blog_inline_contextual_tool'

  // Identify-destination branches (grass-type posts + the Scotts reframe) get the
  // same inline upload treatment as the default: their reader's next step is a
  // photo, so open the picker in place instead of linking to the homepage.
  if (isIdentify) {
    return (
      <InlineScanCta
        ctaLocation={ctaLocation}
        heading={identifyV2Copy?.heading ?? tool.label}
        tagline={identifyV2Copy?.tagline ?? tool.tagline}
        previewChips={showIdentifyV2 ? IDENTIFY_V2_PREVIEW_CHIPS : undefined}
        // Preserve the historical cta_text for these branches (they reported
        // tool.label via identifyCtaProps({ text }) before the upload rework).
        analyticsText={tool.label}
        analyticsProps={identifyAnalyticsProps}
      />
    )
  }

  // Diagnose branch (V2): an in-place photo picker instead of a link banner.
  // Symptom-keyed heading + deliverable chips; the picked photo auto-starts on
  // /diagnose via the destination handoff. cta_location is UNCHANGED
  // (blog_inline_diagnose); cta_variant + symptom_key ride as additive props.
  if (isDiagnose) {
    // The weed fallback keys off a synthetic symptom rather than whatever
    // generic stem the post's taxonomy happens to carry, so it gets weed copy
    // and stays segmentable in PostHog.
    const symptomKey = isWeedFallback
      ? WEED_FALLBACK_SYMPTOM_KEY
      : matchedDiagnoseKeyword(slug, category, tags)
    return (
      <InlineScanCta
        ctaLocation={ctaLocation}
        heading={diagnoseHeadingFor(symptomKey)}
        tagline={DIAGNOSE_INLINE_TAGLINE}
        destination={DIAGNOSE_CTA.href}
        previewChips={DIAGNOSE_PREVIEW_CHIPS}
        ariaLabel={
          isWeedFallback
            ? 'Upload a photo of the weed to diagnose your lawn'
            : 'Upload a photo of the damage to diagnose your lawn'
        }
        analyticsText={DIAGNOSE_CTA.label}
        analyticsProps={{
          cta_variant: 'v2_scan',
          ...(symptomKey ? { symptom_key: symptomKey } : {}),
          ...slotProps,
        }}
      />
    )
  }

  return (
    <ToolLinkBanner
      tool={tool}
      ctaLocation={ctaLocation}
      slug={slug}
      pillLabel={isWeedIdentifier ? 'Identify free' : 'Open tool'}
      analyticsProps={slotProps}
    />
  )
}

/**
 * Link-destination banner (tool / diagnose / weed identifier). Split out of
 * ContextualToolCTA so it can own the impression hook: `ctaLocation` is only
 * known after the parent's early returns, and the hook must not be called
 * conditionally. This is the wrapper pattern useImpressionEvent documents.
 */
function ToolLinkBanner({
  tool,
  ctaLocation,
  slug,
  pillLabel,
  analyticsProps,
}: {
  tool: ToolRec
  ctaLocation: string
  slug?: string
  pillLabel: string
  analyticsProps?: EventProperties
}) {
  // Same bag on impression and click. The click carried no surface or
  // destination before, so a CTR could be computed but never split by tool.
  const ctaProps = ctaSurfaceProps(ctaLocation, {
    surface: 'contextual_tool_cta',
    destination: tool.href,
    slug,
    ...analyticsProps,
  })
  const impressionRef = useImpressionEvent<HTMLAnchorElement>(
    ctaLocation,
    ctaProps,
    { emit: analytics.ctaSurfaceShown },
  )

  return (
    <a
      ref={impressionRef}
      href={tool.href}
      onClick={() => trackEvent('cta_clicked', { ...ctaProps, cta_text: tool.label })}
      className={bannerClass}
    >
      {/* Tool routes get their bespoke illustrated SVG; non-tool destinations
          (diagnose, grass ID) keep their lucide glyph. */}
      {hasToolIllustration(tool.href) ? (
        <div className={`${iconTileClass} [&_svg]:h-9 [&_svg]:w-9`}>
          <ToolIllustration href={tool.href} />
        </div>
      ) : (
        <div className={iconTileClass}>
          <tool.icon className="w-6 h-6 text-green-600" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <span className="text-green-800 font-editorial font-bold text-base md:text-[17px] tracking-tight leading-snug block">
          {tool.label}
        </span>
        <span className="text-gray-600 text-sm leading-normal hidden sm:block">
          {tool.tagline}
        </span>
      </div>
      <div className={ctaPillClass}>
        <span className="hidden sm:inline">{pillLabel}</span>
        <ArrowRight className="w-[18px] h-[18px] group-hover:translate-x-1 transition-transform" />
      </div>
    </a>
  )
}
